import { useToaster } from 'hooks/useToaster';
import { useState } from 'react';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import {
  AgxRow,
  AgxColumn,
  AgxBodyText,
  AgxSlideUpModal,
  AustralianState,
  Breakpoints,
  Images,
  PageStatusType,
  FormPage,
  useWindowSize,
} from '@urbanx/agx-ui-components';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { concatenateAddress } from 'utils/formatAddress';
import { useFormClearer } from 'hooks/useFormCleaner';
import useFormAddressSelector from 'selectors/useFormAddressSelector';
import HomeIconThemed from '../../elements/CampaignsView/assets/icons/homeIconThemed';
import { reloadCampaigns } from '../../campaigns/campaignsReducer';
import { PrefillSelector } from './components/PrefillSelector';
import { PageTitle } from './components/PageTitle';
import './FormHeader.scss';
import { FormType } from '@urbanx/agx.campaigns.client';
import { AgxSpacer, AgxTypography, Icon_CheckmarkOutline } from '@urbanx/agx.ui-components2';

interface FormHeaderProps {
  currentPage: FormPage;
  pages: FormPage[];
  setPage: (pageNumber: number) => void;
  displayTitle?: string;
  viewPreFill?: boolean;
  showListingDetails?: boolean;
  showBreadcrumbs?: boolean;
  listingAddress?: string;
  saveForm: () => Promise<void>;
  formTypeValue?: FormType;
}

export const FormHeader = ({
  currentPage,
  pages,
  setPage,
  displayTitle,
  viewPreFill,
  showListingDetails,
  showBreadcrumbs,
  listingAddress,
  saveForm,
  formTypeValue,
}: FormHeaderProps) => {
  const formAddress = useFormAddressSelector();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { showErrorToast } = useToaster();
  const clearFormData = useFormClearer();
  const {
    pageStatuses,
    selectedForm,
    formName: configuredFormName,
  } = useAppSelector((state) => state.form);
  const [showBreadcrumbsSlider, setShowBreadcrumbsSlider] = useState(false);

  const formName = (formTypeValue?: FormType) => {
    switch (formTypeValue) {
      case FormType.ListingProposal:
        return 'Listing Proposal';
      case FormType.ListingGenerator:
        return 'Agreement';
      case FormType.ListingSubmission:
        return 'Listing Submission';
      case FormType.ContractRequest:
        return selectedForm?.state === AustralianState.NSW ? configuredFormName : 'Contract';
      case FormType.ContractExchange:
        return selectedForm?.state === AustralianState.NSW
          ? configuredFormName
          : 'Contract Exchange';
      case FormType.ContractSubmission:
        return 'Contract Submission';
      default:
        return '';
    }
  };

  const { streetAddress, suburb, unitNumber, postalCode, state } = formAddress ?? {};

  const address = concatenateAddress([unitNumber, streetAddress, suburb, postalCode, state], ', ');

  const {
    name: currentPageName,
    title,
    displayPreFill,
    displayListingDetails,
    exitButtonInHeader,
  } = currentPage;

  const filteredPagesWithCurrentPageName = pages
    ?.map((page, pageNumber) => {
      return {
        name: page.name,
        isActive: page.name === currentPageName,
        pageNumber: pageNumber,
        hasBreadcrumbs: page.hasBreadcrumbs,
      };
    })
    ?.filter((pageDetails) => pageDetails.hasBreadcrumbs);

  const pagesInProgress = filteredPagesWithCurrentPageName?.map(
    ({ isActive, name, pageNumber }, orderedPageNumber) => {
      const pageStatus =
        pageStatuses?.find((p) => p.pageName === name)?.status ?? PageStatusType.Future;
      const classes = clsx('agx-progress-item', isActive ? 'active' : pageStatus);
      const shouldShowCompleteIcon = !isActive && pageStatus === PageStatusType.Complete;
      const shouldShowStepNumber = !shouldShowCompleteIcon;

      return (
        <div key={name} className={classes} onClick={() => setPage(pageNumber)}>
          <div className="agx-progress-item-circle">
            <div className="agx-progress-item-text">
              <AgxSpacer fill contentAlignment="center" contentJustification="center">
                {shouldShowStepNumber && (
                  <AgxTypography
                    id={`step-${orderedPageNumber}`}
                    type="body_ui"
                    size="small"
                    color={'var(--text-primary-on-brand)'}
                  >
                    {orderedPageNumber + 1}
                  </AgxTypography>
                )}
                {shouldShowCompleteIcon && <Icon_CheckmarkOutline color="var(--fg-brand-main)" />}
              </AgxSpacer>
            </div>
          </div>
        </div>
      );
    }
  ) ?? <></>;

  const pagesListContent = (
    <AgxColumn>
      {filteredPagesWithCurrentPageName?.map(
        ({ isActive, name, pageNumber }, orderedPageNumber) => {
          const pageStatus =
            pageStatuses?.find((p) => p.pageName === name)?.status ?? PageStatusType.Future;
          return (
            <div
              key={name}
              className={clsx('agx-pageListContainer', isActive && 'isActive')}
              onClick={() => {
                setPage(pageNumber);
                setShowBreadcrumbsSlider(false);
              }}
            >
              <AgxRow centered mediumGap>
                <div className={clsx('progressCircle', isActive ? 'active' : pageStatus)}>
                  <AgxTypography
                    id={`${name}-${pageNumber}-title`}
                    type={'label'}
                    size={'small'}
                    color={isActive ? 'var(--text-primary)' : 'var(--text-brand-main)'}
                  >
                    {orderedPageNumber + 1}
                  </AgxTypography>
                </div>
                <AgxTypography
                  id={`${name}-${pageNumber}-name`}
                  type={'body_ui'}
                  size={'small'}
                  color={'var(--text-primary)'}
                >
                  {name}
                </AgxTypography>
              </AgxRow>
              <Images.ChevronRight />
            </div>
          );
        }
      ) ?? <></>}
    </AgxColumn>
  );

  const handleShowBreadcrumbsSlider = () => {
    setShowBreadcrumbsSlider(!showBreadcrumbsSlider);
  };

  const screenSize = useWindowSize();
  const isMobileOrTabletPortrait =
    screenSize === Breakpoints.Mobile || screenSize === Breakpoints.Tablet_Portrait;

  const validListingAddress = !!(listingAddress ?? address);
  const shouldShowListingDetails = showListingDetails || displayListingDetails;

  const exitForm = async () => {
    try {
      await saveForm();
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Failed to save form.');
      return;
    }
    clearFormData();
    navigate('/');
    dispatch(reloadCampaigns());
  };

  return (
    <div className="formHeader">
      {currentPage && !isMobileOrTabletPortrait && (
        <div className="desktopAndTabletLandscape pageTitle">
          <PageTitle
            onClick={handleShowBreadcrumbsSlider}
            title={displayTitle ?? title}
            breadcrumbsAvailable={showBreadcrumbs}
          />
        </div>
      )}
      {((shouldShowListingDetails && validListingAddress) || showBreadcrumbs) && (
        <>
          {shouldShowListingDetails && showBreadcrumbs && (
            <div className="agx-header-listing-details mobileAndTabletPortrait">
              <AgxBodyText small secondary ellipsisOverflow extraClasses="listing-detail-line">
                {formName(formTypeValue)}
              </AgxBodyText>
              <AgxBodyText small secondary ellipsisOverflow extraClasses="listing-detail-line">
                {listingAddress ?? address}
              </AgxBodyText>
            </div>
          )}
          <div className="iconButtonStyle mobileAndTabletPortrait" onClick={exitForm}>
            <HomeIconThemed />
          </div>
          {showBreadcrumbs && (
            <div className="agx-header-progress mobileAndTabletPortrait">{pagesInProgress}</div>
          )}
        </>
      )}
      {currentPage && isMobileOrTabletPortrait && (
        <div className="mobileAndTabletPortrait pageTitle">
          <PageTitle
            onClick={handleShowBreadcrumbsSlider}
            title={displayTitle ?? title}
            breadcrumbsAvailable={showBreadcrumbs}
          />
        </div>
      )}
      {(viewPreFill || displayPreFill) && <PrefillSelector />}
      {!viewPreFill && exitButtonInHeader && (
        <div className="titleAction">
          <div className="agx-form-header-exit" onClick={exitForm}>
            <Images.CloseOutline className="exit" />
          </div>
        </div>
      )}
      {showBreadcrumbsSlider && (
        <AgxSlideUpModal
          content={pagesListContent}
          onClose={() => setShowBreadcrumbsSlider(false)}
        />
      )}
    </div>
  );
};
